import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Coords } from 'ha/modules/GoogleMap';
import { track } from '@ha/analytics';

import { ListingPhoto } from '@ha/api/v2/types/Listing';
import { Unit } from '@ha/api/v2/types/inventory/UnitType';
import { useUnitTypeId } from 'ha/modules/MultiUnitListings/hooks';

import { RootState, Video, GalleryContentType } from '../types';
import {
  getCity,
  getCityLocalized,
  getCountry,
  getCountryLocalized,
  getListingId,
  getStreet,
  getVideos,
  getHasCoordinates,
  getCoordinates,
  getListingImages,
  getDescription,
  getIsMultiUnit,
  getUnitTypeUnits,
} from '../selectors';

import { GalleryLayout } from '../components/GalleryLayout';
import { galleryOpenedEventName } from '../constants';

interface Props {
  photos: ListingPhoto[];
  floorplanPhotos: ListingPhoto[];
  panoramicPhotos: ListingPhoto[];
  videos: Video[];
  street: string;
  countryLocalized?: string;
  country: string;
  cityLocalized?: string;
  city: string;
  listingId: number;
  hasCoordinates: boolean;
  coordinates: Coords;
  description: string;
  isMultiUnit: boolean;
  units: Unit[];
}

type GalleryImageType =
  | GalleryContentType.Photo
  | GalleryContentType.PanoramicPhoto
  | GalleryContentType.Floorplan;

const GalleryLayoutInternal: React.FC<Props> = ({
  listingId,
  country,
  countryLocalized,
  city,
  cityLocalized,
  photos,
  floorplanPhotos,
  panoramicPhotos,
  hasCoordinates,
  coordinates,
  description,
  isMultiUnit,
  units,
  ...restProps
}) => {
  const [isFullscreenGalleryOpen, setIsFullscreenGalleryOpen] =
    React.useState(false);
  const [initialSlideIndex, setInitialSlideIndex] = React.useState(0);
  const [initialContentName, setInitialContentName] = React.useState<string>();

  const { isUnitTypeId } = useUnitTypeId();

  const analyticsEventProps = React.useMemo(() => {
    if (isUnitTypeId) {
      return {
        listingId: isMultiUnit ? undefined : units?.[0]?.id, // deprecated
        unitId: isMultiUnit ? undefined : units?.[0]?.id,
        unitTypeId: listingId,
        isMultiUnit,
      };
    }

    return {
      listingId,
    };
  }, [isUnitTypeId, listingId, isMultiUnit, units]);

  const handleImageClick = React.useCallback(
    (id: number, type: GalleryImageType) => {
      track(galleryOpenedEventName, {
        ...analyticsEventProps,
        opened_from: 'grid image',
        mediaType: 'image',
      });

      setInitialSlideIndex(id);
      setInitialContentName(type);
      setIsFullscreenGalleryOpen(true);
    },
    [analyticsEventProps],
  );

  const handleContentButtonClick = React.useCallback(
    (type: GalleryContentType) => {
      const mediaTypes = {
        [GalleryContentType.Photo]: 'image',
        [GalleryContentType.Floorplan]: 'floorplan',
        [GalleryContentType.PanoramicPhoto]: '360 photo',
        [GalleryContentType.Video]: 'video',
        [GalleryContentType.Map]: 'map',
        [GalleryContentType.VirtualTour]: '3D Tour',
      };

      track(galleryOpenedEventName, {
        ...analyticsEventProps,
        opened_from: 'media button',
        mediaType: mediaTypes[type],
      });

      setInitialSlideIndex(0);
      setInitialContentName(type);
      setIsFullscreenGalleryOpen(true);
    },
    [analyticsEventProps],
  );

  const handleClose = React.useCallback(() => {
    setIsFullscreenGalleryOpen(false);
  }, []);

  const showFloorplansInGrid = !photos.length && floorplanPhotos.length;

  const imageGridProps = React.useMemo(
    () => ({
      tiles: showFloorplansInGrid ? floorplanPhotos : photos,
      onImageClick: (id: number) =>
        handleImageClick(
          id,
          showFloorplansInGrid
            ? GalleryContentType.Floorplan
            : GalleryContentType.Photo,
        ),
    }),
    [floorplanPhotos, handleImageClick, photos, showFloorplansInGrid],
  );

  return (
    <GalleryLayout
      {...restProps}
      country={countryLocalized || country}
      city={cityLocalized || city}
      isFullscreenGalleryOpen={isFullscreenGalleryOpen}
      initialSlideIndex={initialSlideIndex}
      initialContentName={initialContentName}
      onClose={handleClose}
      onImageButtonClick={() =>
        handleContentButtonClick(GalleryContentType.Photo)
      }
      onPanoramicPhotosButtonClick={() =>
        handleContentButtonClick(GalleryContentType.PanoramicPhoto)
      }
      onVideoButtonClick={() =>
        handleContentButtonClick(GalleryContentType.Video)
      }
      onFloorplanButtonClick={() =>
        handleContentButtonClick(GalleryContentType.Floorplan)
      }
      onMapButtonClick={() => handleContentButtonClick(GalleryContentType.Map)}
      onVirtualTourButtonClick={() =>
        handleContentButtonClick(GalleryContentType.VirtualTour)
      }
      listingId={listingId}
      photos={photos}
      floorplanPhotos={floorplanPhotos}
      panoramicPhotos={panoramicPhotos}
      imageGridProps={imageGridProps}
      hasCoordinates={hasCoordinates}
      coordinates={coordinates}
      description={description}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  const images = getListingImages(state)!.map((image, index) => ({
    ...image,
    id: index, // we need to pass index as id which is used by SwipableProvider.tsx for displaying the current slide
  }));
  const photos = images.filter(
    image => image.type !== 'floorplan' && image.type !== '360',
  );
  const floorplanPhotos = images.filter(image => image.type === 'floorplan');
  const panoramicPhotos = images.filter(image => image.type === '360');

  return {
    photos,
    floorplanPhotos,
    panoramicPhotos,
    videos: getVideos(state) || [],
    street: getStreet(state),
    cityLocalized: getCityLocalized(state),
    countryLocalized: getCountryLocalized(state),
    city: getCity(state),
    country: getCountry(state),
    listingId: getListingId(state),
    hasCoordinates: getHasCoordinates(state),
    coordinates: getCoordinates(state),
    description: getDescription(state),
    isMultiUnit: getIsMultiUnit(state),
    units: getUnitTypeUnits(state),
  };
};

const enhance = compose(connect(mapStateToProps));

const GalleryLayoutContainer = enhance(GalleryLayoutInternal);
export { GalleryLayoutContainer };
// eslint-disable-next-line import/no-default-export
export default GalleryLayoutContainer;
